import { useStore } from '@nanostores/react';
import { accountStore, authStore } from './store';
import type { AugmentedAccount } from './types';

export const AccountFeatureFlag = {
  Whatsapp: 'whatsapp',
  StoreAnonEvents: 'store_anon_events',
  JavascriptWebhook: 'javascript_webhook',
  LegacyCustomSmtpBilling: 'legacy_custom_smtp_billing',
  SubscriptionCenterM1: 'subscription_center_m1',
  MultiLanguageSettings: 'multi_language_settings',
  ExitOnlyAfterMessage: 'exit_only_after_message',
  ParcelAmp: 'parcel_amp',
  CdpDataCatalogEnabled: 'cdp_data_catalog_enabled',
  VirtualSupportChatEnabled: 'virtual_support_chat_enabled',
  ParcelInHydra: 'parcel_in_hydra',
  InAppLegacyEditor: 'in_app_legacy_editor',
  TourtipAdventuresV1: 'tourtip_adventures_v1',
  FlagCDPUIConsolidation: 'cdp_ui_consolidation',

  DarkModeToggle: 'dark-mode-toggle',
  GoalsMilestones: 'goals-milestones',
  McpExternalAccountToggle: 'mcp-external-account-toggle',
  TasksPerformanceDashboard: 'tasks_performance_dashboard_enabled',
  SegmentAutoArchive: 'segment_auto_archive',
  JourneysTaxonomyRename: 'journeys_taxonomy_rename',
  JourneysTaxonomyRenameFinal: 'journeys_taxonomy_rename_final',
  // Pulled from Ember feature flags
  ActivityLogsTimeRange: 'activity-logs-time-range',
  DryadGoalsBuilder: 'dryad-goals-builder',
  AiAutoSegmentNames: 'ai_auto_segment_names',
  DeliverabilityAnalytics: 'deliverability-analytics',
  TransactionalSms: 'transactional-sms',
  NewsletterRateLimitWithTimezone: 'newsletter-rate-limit-timezone',
  InAppEditorLeadForm: 'in-app-editor-lead-form',
  DefaultFromIdentity: 'default-from-identity',
  EmailAutoTls: 'email-auto-tls',
  ConfiguredWebhooks: 'configured_webhooks',
  LLMActionBYOK: 'llm-action-byok',
  LLMActionActivationExperiment: 'llm-action-activation-experiment',
  LLMActionKnowledgeSources: 'llm-action-knowledge-sources',
  InAppForms: 'in-app-forms',
  TeamMembersExport: 'team_member_export',
  GlobalExitConditions: 'global-exit-conditions',
  ObjectWaitUntil: 'obj_wait_until',
  JourneyAttributes: 'journey-attributes',
  WhatsappNativeFreeText: 'whatsapp-native-free-text',
  ShowTemplatesSnippetsLayouts: 'show_templates_snippets_layouts',
  InAppGlobalStyles: 'in-app_global-styles',
  HalloweenStickies2025: 'halloween_stickies_2025',
  OptimizedMessageLibrary: 'optimized-message-library',

  DsSocialIcons: 'ds-social-icons',
  DsCountdown: 'ds-countdown',
  DsVideo: 'ds-video',
  DsUiPlayground: 'ds-ui-playground',
  DsDashboardInHydra: 'ds-dashboard-in-hydra',
  DsVirtualizedDashboardTree: 'ds-virtualized-dashboard-tree',

  InAppTooltips: 'in-app-tooltips',
  InAppLivePreview: 'in-app_live-preview',
  DsInfiniteCanvas: 'ds-infinite-canvas',
  DsUseTiptap: 'ds-use-tiptap',
  PausePlanM2: 'pause-plan-m2',
  PavedRoad: 'paved_road',

  MultipleShortlinkDomains: 'multiple_shortlink_domains',
  NotificationsUx: 'notifications-ux',
  HomeUx: 'home-ux',
  ServiceAccounts: 'service-accounts',
  TestDeliveryMode: 'test_delivery_mode',
  TemplateEngineSwitch: 'template_engine_switch',
  AccountVerification: 'account-verification',
  PushV2: 'push_v2',
  MultiApp: 'multi_app',
  DsLiquidExpressions: 'ds-liquid-expressions',
  Gravatar: 'gravatar',
  GravatarEnabled: 'gravatar_enabled',
  AllowAgentEditLiveData: 'allow_agent_edit_live_data',
  McpAllowEditLiveData: 'mcp_allow_edit_live_data',
  McpAllowAccessSensitiveData: 'mcp_allow_access_sensitive_data',
  AgentSnowflake: 'agent_snowflake',
  CdpSalesforceEcaMigration: 'cdp_salesforce_eca_migration',
  CdpSalesforceEcaMigrationOptout: 'cdp_salesforce_eca_migration_optout',
  CdpTrackEnabled: 'cdp_track_enabled',
  FrequencyCaps: 'frequency_caps',
  DateTriggeredObjectCampaigns: 'date-triggered-object-campaigns',
  EditorBreadcrumbs: 'editor-breadcrumbs',
  GeoFencingEnabled: 'geo_fencing_enabled',
  DsInsertMenuPreviews: 'ds-insert-menu-previews',
  DsRecommendedProfiles: 'ds-recommended-profiles',
  FlappyMail: 'flappy-mail',
  AgentCampaignHighlights: 'agent-campaign-highlights',
  PhoneIdentifier: 'phone-identifier',
  CustomSms: 'custom-sms',
  OpenTrackingConsent: 'open_tracking_consent',
  HydraBilling: 'hydra-billing',
  ProfileGrowth: 'profile_growth',
  NewPipelinesObjectsPricing: 'new_pipelines_objects_pricing',
} as const;

export type AccountFeatureFlag = (typeof AccountFeatureFlag)[keyof typeof AccountFeatureFlag];

export type FeatureFlag = AccountFeatureFlag;
export const FeatureFlag = { ...AccountFeatureFlag };

export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = Object.freeze({
  whatsapp: false, // whether to enable sending to whatsapp numbers in Twilio SMS action
  store_anon_events: true, // enables the collection of anonymous events.
  javascript_webhook: false, // enables adding javascript reporting webhook
  legacy_custom_smtp_billing: true, // whether to skip message overages for accounts with custom smtp https://github.com/customerio/issues/5947
  subscription_center_m1: false, // enables milestone 0 and milestone 1 features of the subscription center
  multi_language_settings: false, // enables custom multi-language settings
  exit_only_after_message: false, // enables the "exit only after receiving a message" exit option for campaigns
  parcel_amp: true, // enables AMP in Parcel
  cdp_data_catalog_enabled: false, // enabled Data Catalog menu item and page in CDP
  virtual_support_chat_enabled: false, // enables the virtual support chat via Zendesk widget
  parcel_in_hydra: false, // enables Parcel in Hydra
  in_app_legacy_editor: false, // enables the legacy in-app editor
  cdp_ui_consolidation: false, //if enabled, CDP is now presented inside the "Integrations" and no sidebar is displayed
  tourtip_adventures_v1: true,

  'dark-mode-toggle': false, // whether to show a dark mode toggle in the UI
  'goals-milestones': false, // enables the goals milestones feature
  'mcp-external-account-toggle': false, // mcp external account toggle
  tasks_performance_dashboard_enabled: false, // enables the tasks performance dashboard
  segment_auto_archive: false, // enables auto-archive behavior for unused segments
  journeys_taxonomy_rename: false, // enables the Journeys taxonomy rename scaffolding
  journeys_taxonomy_rename_final: false, // enables the "final" rename state (renamed language, no transitional tooltips/parentheses)
  // Ember feature flags
  'activity-logs-time-range': false, // enables the activity logs time range feature
  'dryad-goals-builder': false, // enables the dryad-based condition builder in the goal edit drawer
  ai_auto_segment_names: false, // enables the AI auto segment names feature
  'deliverability-analytics': false, // enables the deliverability analytics feature
  'transactional-sms': false, // enables the transactional SMS feature
  'newsletter-rate-limit-timezone': false, // enables the newsletter rate limit timezone feature
  'in-app-editor-lead-form': false, // enables the in-app editor lead form feature
  'default-from-identity': false, // enables the default from identity feature
  'email-auto-tls': false, // enables the email auto TLS feature
  configured_webhooks: false, // enables the configured webhooks feature
  'llm-action-byok': false, // enables the LLM action BYOK feature
  'llm-action-activation-experiment': false, // enables the LLM action activation experiment
  'llm-action-knowledge-sources': false, // enables the LLM action knowledge sources feature
  'in-app-forms': false, // enables the in-app forms feature
  team_member_export: false, // enables the team member export feature
  'global-exit-conditions': false, // enables the global exit conditions feature
  obj_wait_until: false, // enables the obj wait until feature
  'journey-attributes': false, // enables the journey attributes feature
  'whatsapp-native-free-text': false, // enables the WhatsApp native free text feature
  show_templates_snippets_layouts: false, // enables the show templates snippets layouts feature
  'in-app_global-styles': false, // enables the in-app global styles feature
  halloween_stickies_2025: false, // enables the Halloween stickies 2025 feature
  'optimized-message-library': false, // enables the optimized message library feature
  'ds-social-icons': false, // enables the social icons component feature
  'ds-countdown': false, // enables the countdown component feature
  'ds-video': false, // enables the video component feature
  'ds-ui-playground': false, // enables the design studio UI playground
  'ds-dashboard-in-hydra': false, // Hydra owns the Design Studio dashboard route; off = the embedded Design Studio bundle serves it (CON-7495 kill-switch)
  'ds-virtualized-dashboard-tree': false, // Virtualize the whole Design Studio dashboard tree via a flattened visible list (parcel parity, CON-7496)

  'in-app-tooltips': false, // enables the in-app tooltips feature
  'in-app_live-preview': false, // enables the in-app live preview feature
  'ds-infinite-canvas': false, // enables the infinite canvas visual editor in Design Studio
  'ds-use-tiptap': false, // enables the tiptap-based visual editor in Design Studio
  'pause-plan-m2': false, // enables the real implementation of account pausing
  paved_road: false, // enables the CDP paved road onboarding experience
  multiple_shortlink_domains: false, // enables the multiple shortlink domains feature
  'notifications-ux': false, // enables the new notifications drawer UX
  'home-ux': false, // enables the home UX feature
  'service-accounts': false, // enables the service accounts UI on the API credentials page
  test_delivery_mode: true,
  template_engine_switch: false, // lets a workspace switch its default template engine (liquid version) even without can_update_default_template_engine
  'account-verification': false,
  push_v2: false, // Delivery-per-device push sending
  multi_app: false, // enables multiple mobile apps for push
  'ds-liquid-expressions': false, // enables liquid conditional logic in Design Studio
  gravatar: false, // enables the gravatar feature
  gravatar_enabled: false, // whether external avatars (e.g. Gravatar) are fetched for the account
  allow_agent_edit_live_data: false, // default off — admins opt in from Privacy & AI to let AI agents edit live data
  mcp_allow_edit_live_data: false, // backend mirrors allow_agent_edit_live_data via a dynamic default
  mcp_allow_access_sensitive_data: false, // off by default — admins opt in from AI & MCP settings
  agent_snowflake: false, // enables the direct Snowflake query endpoint for AI agents
  cdp_salesforce_eca_migration: false,
  cdp_salesforce_eca_migration_optout: false, // when on, treats cdp_salesforce_eca_migration as off (lets accounts opt out of the ECA migration)
  cdp_track_enabled: false,
  frequency_caps: false,
  'date-triggered-object-campaigns': false,
  'editor-breadcrumbs': false, // enables Hydra breadcrumbs on editor/composer pages
  geo_fencing_enabled: false, // enables the geofencing feature
  'ds-insert-menu-previews': false, // enables insert menu previews in Design Studio
  'ds-recommended-profiles': false, // shows recommended profiles + highlights liquid syntax inline in Design Studio Preview Mode
  'flappy-mail': false, // enables the Flappy Mail game on the home page
  'agent-campaign-highlights': false, // highlights agent-added/edited workflow nodes in the visual workflow builder and listens for per-action cache invalidations
  'phone-identifier': false, // enables using a phone number as a profile identifier
  'custom-sms': false, // enables Custom SMS vendor settings page
  open_tracking_consent: false, // enables the "only track opens from people who have consented" workspace setting
  'hydra-billing': false, // enables the billing UI to be rendered from hydra instead of ember
  profile_growth: false, // enables the Profile growth tab on the Plans & Billing page
  new_pipelines_objects_pricing: false, // enables the "new pipelines objects pricing" plan highlights on the Your Plan page
});

const useFeatureFlags = () => {
  const account = useStore(accountStore);
  const flags = account?.feature_flags ?? {};

  return Object.assign({}, FEATURE_FLAG_DEFAULTS, flags);
};

export const useFeatureFlag = (flag: FeatureFlag | string) => {
  const flags = useFeatureFlags();
  return flags[flag] ?? false;
};

/**
 * Pure flag check for an explicit account. When the account is null (not
 * loaded yet), the flag's registered default applies — callers that render
 * before auth completes must treat that answer as provisional and re-evaluate
 * once the account arrives (subscribe to `accountStore`, or use a reactive
 * hook like `useFeatureFlag`).
 */
export const accountHasFeatureFlag = (
  account: Pick<AugmentedAccount, 'feature_flags'> | null | undefined,
  flag: FeatureFlag,
) => {
  const defaultValue = FEATURE_FLAG_DEFAULTS[flag] ?? false;

  if (account == null) {
    return defaultValue;
  }

  return account.feature_flags?.[flag] ?? defaultValue;
};

/**
 * Call-time snapshot of `accountHasFeatureFlag` against the current auth
 * store. Safe in event handlers and other post-auth code paths; do NOT use
 * during render for anything that gets cached — the pre-auth answer is the
 * flag default and won't recompute (see `useFeatureFlag`).
 */
export const hasFeatureFlag = (flag: FeatureFlag) => {
  return accountHasFeatureFlag(authStore.get()?.account, flag);
};
